import { Add, Delete, Draw, Edit, Home, Refresh, Search } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { COLORS } from "assets";
import { BoxSelect, BulkActionBar, DataTable, KebabMenu, StatusPill, StyledCard, bulkCheckboxSx, bulkDeleteButtonSx, bulkPrimaryButtonSx } from "components";
import StyledTablePagination from "components/StyledTablePagination";
import { CAMERA_STATUS_LABELS, ENDPOINTS, filterModelsForUI, UI_MODELS } from "config/constants";
import { useContextController } from "context";
import { useFetch, useTitle } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import { ChangeEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BoxModel, CameraModel, ModelTypes } from "types";
import { canManageAnySite, canManageSite } from "utils";
import AddCamera from "./add";
import BulkRemoveCameras from "./bulk-remove";
import EditCamera from "./edit";
import BulkManageCameras from "./manage-bulk";
import RemoveCamera from "./remove";

interface Data {
  cameras?: CameraModel[];
  totalRecords?: number;
}

interface ICameraRowActions {
  camera: CameraModel;
  onEdit: (camera: CameraModel) => void;
  onRemove: (camera: CameraModel) => void;
}

function CameraRowActions({ camera, onEdit, onRemove }: ICameraRowActions) {
  const t = useTranslation();
  const navigate = useNavigate();

  return (
    <KebabMenu
      items={[
        {
          key: "roi",
          label: t["Edit ROI"],
          icon: <Draw fontSize="small" />,
          onClick: () => navigate("/cameras/" + camera._id)
        },
        {
          key: "edit",
          label: t["Edit"],
          icon: <Edit fontSize="small" />,
          onClick: () => onEdit(camera)
        },
        {
          key: "delete",
          label: t["Delete"],
          icon: <Delete fontSize="small" />,
          destructive: true,
          onClick: () => onRemove(camera)
        }
      ]}
    />
  );
}

const actionButtonSx = {
  bgcolor: COLORS.surface.card,
  color: COLORS.surface.primaryText,
  border: `1px solid ${COLORS.surface.border}`,
  borderRadius: `${COLORS.radius.sm}px`,
  "&:hover": { bgcolor: COLORS.surface.tableHeader }
};

export default function Cameras() {
  const [{ userData }] = useContextController();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<CameraModel>();
  const [removing, setRemoving] = useState<CameraModel>();
  const [bulkManage, setBulkManage] = useState(false);
  const [bulkRemove, setBulkRemove] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [boxId, setBox] = useState<string>();
  const [selectedBox, setSelectedBox] = useState<BoxModel>();
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<keyof CameraModel>();
  const [isAscending, setAscending] = useState(false);

  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchGet } = useFetch<Data>("GET");
  const { cameras, totalRecords } = useMemo(() => {
    return { cameras: response?.data?.cameras || [], totalRecords: response?.data?.totalRecords || 0 };
  }, [response]);

  const selectedSiteId =
    selectedBox?.siteId || selectedBox?.site?._id || cameras[0]?.box?.siteId || cameras[0]?.box?.site?._id;
  const canManageSelected = canManageSite(userData, selectedSiteId);
  const enabledModels: ModelTypes[] = useMemo(() => {
    const fromBox = selectedBox?.site?.enabledModels;
    const fromCamera = cameras[0]?.box?.site?.enabledModels;
    if (Array.isArray(fromBox)) return filterModelsForUI(fromBox);
    if (Array.isArray(fromCamera)) return filterModelsForUI(fromCamera);
    return [...UI_MODELS];
  }, [selectedBox, cameras]);

  const searchWaitRef = useRef<NodeJS.Timeout>();
  const onSort = useCallback(
    (val: keyof CameraModel) => {
      if (val === sort) {
        if (isAscending) {
          setAscending(false);
        } else {
          setSort(undefined);
        }
      } else {
        setSort(val);
        setAscending(true);
      }
    },
    [sort, isAscending]
  );
  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  const onPageChange = (_: any, page: number) => setPage(page);
  const onSearchChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ target: { value } }) =>
    setSearch(value);
  const onRowsPerPageChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ target: { value } }) => {
    setLimit(Number.parseInt(value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!boxId) return;
    let params = `?limit=${limit}&page=${page + 1}&boxId=${boxId}`;
    if (search) {
      params += `&search=${search}`;
    }
    if (sort) {
      if (isAscending) {
        params += `&sort=${sort}`;
      } else {
        params += `&sort=-${sort}`;
      }
    }
    if (searchWaitRef.current) {
      clearTimeout(searchWaitRef.current);
    }
    searchWaitRef.current = setTimeout(() => {
      callFetchGet(ENDPOINTS.cameras + params).catch(console.error);
    }, 300);
  }, [page, limit, search, sort, isAscending, boxId, reload, callFetchGet]);

  useEffect(() => setPage(0), [search, boxId]);
  useEffect(() => setSelectedIds([]), [page, limit, search, sort, isAscending, boxId]);

  useTitle("Cameras");

  const manageableIds = useMemo(
    () =>
      cameras
        .filter((camera) => canManageSite(userData, camera.box?.siteId || camera.box?.site?._id))
        .map((camera) => camera._id),
    [cameras, userData]
  );
  const selectedCameras = useMemo(
    () => cameras.filter((camera) => selectedIds.includes(camera._id)),
    [cameras, selectedIds]
  );
  const allManageableSelected =
    manageableIds.length > 0 && manageableIds.every((id) => selectedIds.includes(id));
  const someManageableSelected = manageableIds.some((id) => selectedIds.includes(id));

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (manageableIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !manageableIds.includes(id));
      }
      return Array.from(new Set([...prev, ...manageableIds]));
    });
  }, [manageableIds]);

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const columns = useMemo(
    () => [
      ...(canManageSelected
        ? [
            {
              header: (
                <Checkbox
                  size="small"
                  checked={allManageableSelected}
                  indeterminate={someManageableSelected && !allManageableSelected}
                  onChange={toggleAll}
                  disabled={!manageableIds.length}
                  sx={bulkCheckboxSx}
                />
              ) as unknown as string,
              align: "center" as const,
              accessor: (row: CameraModel) => {
                const manage = canManageSite(userData, row.box?.siteId || row.box?.site?._id);
                if (!manage) return null;
                return (
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(row._id)}
                    onChange={() => toggleOne(row._id)}
                    sx={bulkCheckboxSx}
                  />
                );
              }
            }
          ]
        : []),
      {
        header: t["ID"],
        accessor: "cameraId" as const
      },
      {
        header: t["Name"],
        accessor: "name" as const
      },
      {
        header: t["RTSP"],
        accessor: "rtspHost" as const
      },
      {
        header: t["RTSP Source"],
        accessor: (row: CameraModel) =>
          row.rtspSource ? (
            <Typography textTransform="capitalize">{row.rtspSource}</Typography>
          ) : row.eagleEyeToken ? (
            "Managed by Eagle Eye"
          ) : row.acc_token ? (
            "Managed by VXG Cloud"
          ) : (
            "Manual"
          )
      },
      {
        header: t["Box"],
        accessor: (row: CameraModel) => row.box?.deviceId
      },
      {
        header: t["Status"],
        align: "center" as const,
        accessor: (row: CameraModel) => {
          const status = row.cameraStatus || "disconnected";
          return <StatusPill label={CAMERA_STATUS_LABELS[status]} status={status} />;
        },
        sort: "cameraStatus" as const
      },
      ...(canManageAnySite(userData)
        ? [
            {
              header: t["Actions"],
              align: "center" as const,
              accessor: (row: CameraModel) => {
                const manage = canManageSite(userData, row.box?.siteId || row.box?.site?._id);
                return manage ? (
                  <Stack direction="row" justifyContent="center" alignItems="center">
                    <CameraRowActions camera={row} onEdit={setEditing} onRemove={setRemoving} />
                  </Stack>
                ) : null;
              }
            }
          ]
        : [])
    ],
    [
      t,
      userData,
      canManageSelected,
      allManageableSelected,
      someManageableSelected,
      manageableIds.length,
      selectedIds,
      toggleAll,
      toggleOne
    ]
  );

  return (
    <PrivateLayout canAccess={() => true}>
      {canManageSelected && adding && <AddCamera onSuccess={onReload} onClose={() => setAdding(false)} />}
      {editing && canManageSite(userData, editing.box?.siteId || editing.box?.site?._id) && (
        <EditCamera camera={editing} onSuccess={onReload} onClose={() => setEditing(undefined)} />
      )}
      {removing && canManageSite(userData, removing.box?.siteId || removing.box?.site?._id) && (
        <RemoveCamera removing={removing} onSuccess={onReload} onClose={() => setRemoving(undefined)} />
      )}
      {bulkManage && selectedCameras.length > 0 && (
        <BulkManageCameras
          cameras={selectedCameras}
          enabledModels={enabledModels}
          onSuccess={() => {
            setSelectedIds([]);
            onReload();
          }}
          onClose={() => setBulkManage(false)}
        />
      )}
      {bulkRemove && selectedCameras.length > 0 && (
        <BulkRemoveCameras
          cameras={selectedCameras}
          onSuccess={() => {
            setSelectedIds([]);
            onReload();
          }}
          onClose={() => setBulkRemove(false)}
        />
      )}

      <Box width="100%" display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2} flexWrap="wrap">
        <Breadcrumbs aria-label="breadcrumb">
          <Tooltip title={t["Home"]}>
            <IconButton LinkComponent={Link} {...{ to: "/" }} size="small" sx={{ color: COLORS.surface.secondaryText }}>
              <Home fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography color="text.primary">{t["Cameras"]}</Typography>
        </Breadcrumbs>
        <Box sx={{ width: { xs: "100%", sm: 300 }, maxWidth: "100%" }}>
          <BoxSelect
            value={boxId}
            onBox={setSelectedBox}
            onChange={(id) => {
              setBox(id);
              if (id) {
                localStorage.setItem("boxId", id);
              }
            }}
            hideLabel
          />
        </Box>
      </Box>

      <Box width="100%">
        <StyledCard>
          <CardHeader
            title={t["Smart Cameras"]}
            titleTypographyProps={{
              fontWeight: 600,
              sx: { color: COLORS.surface.primaryText, fontSize: 16 }
            }}
            action={
              <Stack direction="row" spacing={0.5} alignItems="center">
                {canManageSelected ? (
                  <Tooltip title={t["Add Camera"]}>
                    <IconButton onClick={() => setAdding(true)} sx={actionButtonSx}>
                      <Add />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {isLoading ? (
                  <IconButton disabled sx={actionButtonSx}>
                    <CircularProgress size={20} sx={{ color: COLORS.primary }} />
                  </IconButton>
                ) : (
                  <Tooltip title={t["Refresh"]}>
                    <IconButton onClick={onReload} sx={actionButtonSx}>
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            }
            sx={{ px: 3, pt: 2.5, pb: 0 }}
          />
          <CardContent sx={{ px: 3, pt: 2, pb: 1.5 }}>
            <TextField
              value={search}
              onChange={onSearchChange}
              id="cameras-search"
              name="cameras-search"
              placeholder={t["Search Cameras"]}
              size="small"
              fullWidth
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  bgcolor: COLORS.surface.card,
                  color: COLORS.surface.primaryText,
                  borderRadius: `${COLORS.radius.md}px`,
                  "& fieldset": { borderColor: COLORS.surface.border },
                  "&:hover fieldset": { borderColor: COLORS.surface.secondaryText },
                  "&.Mui-focused fieldset": { borderColor: COLORS.primary }
                },
                "& .MuiInputBase-input::placeholder": { color: COLORS.surface.mutedText, opacity: 1 }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: COLORS.surface.secondaryText }} />
                  </InputAdornment>
                )
              }}
            />
            <BulkActionBar
              count={selectedIds.length}
              label={selectedIds.length === 1 ? "camera selected" : "cameras selected"}
              onClear={() => setSelectedIds([])}
              clearLabel={t["Clear Selection"] || "Clear Selection"}
            >
              <Button variant="contained" onClick={() => setBulkManage(true)} sx={bulkPrimaryButtonSx}>
                {t["Manage Models"] || "Manage Models"}
              </Button>
              {canManageSelected ? (
                <Button variant="contained" onClick={() => setBulkRemove(true)} sx={bulkDeleteButtonSx}>
                  {t["Delete"]}
                </Button>
              ) : null}
            </BulkActionBar>
            <DataTable
              rows={cameras || null}
              isLoading={isLoading}
              error={error}
              sort={sort}
              isAscending={isAscending}
              onSort={onSort}
              columns={columns}
              selectedIds={selectedIds}
              getRowId={(row) => row._id}
            />
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
            <StyledTablePagination
              component="div"
              align="right"
              count={totalRecords || 0}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          </CardActions>
        </StyledCard>
      </Box>
    </PrivateLayout>
  );
}
